import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import './imageOverlay.css';
import Plus from "assets/Plus.svg";
import Minus from "assets/Minus.svg";

export const ImageOverlay = ({ imageUrl, onClose }) => {
  const [scale, setScale] = useState(1);
  const containerRef = useRef(null);
  const modalRef = useRef(null);
  const closeBtnRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  // Move focus into the dialog on open and restore it to the trigger on close.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    closeBtnRef.current?.focus();
    return () => {
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, []);

  // The image is sized relative to the scroll container (100% = fits the
  // available width and height), so scale 1 always fits and anything above
  // overflows into a scrollable region.
  const zoom = (newScale) => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const { clientWidth, clientHeight, scrollLeft, scrollTop } = container;

    const imgCenterX = (scrollLeft + clientWidth / 2) / scale;
    const imgCenterY = (scrollTop + clientHeight / 2) / scale;

    setScale(newScale);

    container.scrollLeft = Math.max(0, imgCenterX * newScale - clientWidth / 2);
    container.scrollTop = Math.max(0, imgCenterY * newScale - clientHeight / 2);
  };

  const zoomIn = () => zoom(Math.min(scale + 0.1, 5));
  const zoomOut = () => zoom(Math.max(scale - 0.1, 0.5));

  const handleClose = () => {
    setScale(1);
    onClose();
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    dragStart.current = {
      x: e.pageX,
      y: e.pageY,
      scrollLeft: containerRef.current.scrollLeft,
      scrollTop: containerRef.current.scrollTop,
    };
    containerRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    const dx = e.pageX - dragStart.current.x;
    const dy = e.pageY - dragStart.current.y;
    containerRef.current.scrollLeft = dragStart.current.scrollLeft - dx;
    containerRef.current.scrollTop = dragStart.current.scrollTop - dy;
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    containerRef.current.style.cursor = 'grab';
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      handleClose();
      return;
    }
    if (e.key === "Tab" && modalRef.current) {
      const focusable = modalRef.current.querySelectorAll(
        'button, [href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const handleOverlayClick = (e) => {
    // Close modal when clicking on the overlay (background)
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleModalClick = (e) => {
    // Prevent clicks inside the modal from bubbling up to the overlay
    e.stopPropagation();
  };

  // Portalled to <body> so the overlay never inherits its host's stacking or
  // interactivity. It is opened from question previews that are deliberately
  // pointer-events:none (the picker's thumbnails) and from inside Bootstrap modals
  // — as a descendant it came up unclickable in the first and behind the second.
  return createPortal((
    <div className="img-overlay" onClick={handleOverlayClick} onKeyDown={handleKeyDown}>
      <div
        className="img-modal"
        ref={modalRef}
        onClick={handleModalClick}
        role="dialog"
        aria-modal="true"
        aria-label="Enlarged question image"
      >
        <div
          className="img-modal-content"
          ref={containerRef}
          style={{ overflow: 'auto' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div className="img-wrapper">
            <img
              src={imageUrl}
              alt="Zoomable"
              style={{
                width: `${scale * 100}%`,
                height: `${scale * 100}%`,
                display: 'block',
                objectFit: "contain"
              }}
            />
          </div>
        </div>
        <div className="img-controls">
          <button type="button" className="img-control-btn" aria-label="Close image" ref={closeBtnRef} onClick={handleClose}>
            <FontAwesomeIcon size="lg" icon={faTimes} />
          </button>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <button type="button" className="img-control-btn" aria-label="Zoom in" onClick={zoomIn}>
              <img src={Plus} alt="" style={{ margin: "-2px" }}/>
            </button>
            <button type="button" className="img-control-btn" aria-label="Zoom out" onClick={zoomOut}>
              <img src={Minus} alt="" style={{ margin: "5px" }}/>
            </button>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
};
